/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import type React from 'react';

import { Checkbox } from '../checkbox';
import { CleanIconButton } from '../clean-icon-button';
import { ConditionalWrap } from '../conditional-wrap';
import { ChevronDownIconOutline } from '../icons/generated/heroIcons/ChevronDownIconOutline';
import { ChevronRightIconOutline } from '../icons/generated/heroIcons/ChevronRightIconOutline';
import { Skeleton } from '../skeleton';
import { Typography } from '../typography';
import {
  type FlattenedTreeItem,
  type TextItemData,
  type TreeItemComponentProps,
} from './tree-view-types';
import { TreeItemWrapper } from './TreeItemWrapper';

/**
 * checks if any parent is checked
 * @param item
 * @param items
 * @returns
 */
const parentIsCheckedRecursively = (
  item: FlattenedTreeItem<TextItemData>,
  items: FlattenedTreeItem<TextItemData>[],
): boolean => {
  const parent = items.find((i) => i.id === item.parentId);
  if (parent) {
    if (parent.isSelected) {
      return true;
    }
    return parentIsCheckedRecursively(parent, items);
  }
  return false;
};

/**
 * checks if any children are checked
 * @param item
 * @param items
 * @returns
 */
const childrenCheckedRecursively = (
  item: FlattenedTreeItem<TextItemData>,
  items: FlattenedTreeItem<TextItemData>[],
): boolean => {
  const children = items.filter((i) => i.parentId === item.id);
  if (children.length === 0) {
    return false;
  }
  return children.some(
    (i) => i.isSelected || childrenCheckedRecursively(i, items),
  );
};

export const TreeViewTextItem = <T extends TextItemData>({
  depth,
  shouldDisableInteraction,
  shouldDisableSorting,
  isGhost,
  indentationWidth,
  isIndicator,
  isCollapsed,
  onCollapse,
  item,
  parent,
  isOver,
  isOverParent,
  setNodeRef,
  setActivatorNodeRef,
  dragHandleProps,
  style,
  trails,
  onItemsChanged,
  items,
  tabIndex,
  ref,
  ...restProps
}: TreeItemComponentProps<T> & { ref?: React.Ref<HTMLElement> }) => {
  const isAnyParentChecked = parentIsCheckedRecursively(item, items);
  const isAnyChildChecked = childrenCheckedRecursively(item, items);

  const isIndeterminate =
    isAnyChildChecked === true &&
    isAnyParentChecked === false &&
    item.isSelected === false
      ? true
      : false;

  return (
    <TreeItemWrapper
      depth={depth}
      shouldDisableInteraction={shouldDisableInteraction}
      shouldDisableSorting={shouldDisableSorting}
      isGhost={isGhost}
      indentationWidth={indentationWidth}
      isIndicator={isIndicator}
      isCollapsed={isCollapsed}
      onCollapse={onCollapse}
      item={item}
      parent={parent}
      isOver={isOver}
      isOverParent={isOverParent}
      setNodeRef={setNodeRef}
      setActivatorNodeRef={setActivatorNodeRef}
      dragHandleProps={dragHandleProps}
      style={style}
      trails={trails}
      // oxlint-disable-next-line @typescript-eslint/no-empty-function
      onItemsChanged={() => {}}
      ref={ref}
      items={items}
      tabIndex={tabIndex}
      {...restProps}
    >
      {item.canHaveSubItems && onCollapse && (
        <button
          type="button"
          className="ndl-tree-view-collapse-button"
          onClick={() => onCollapse()}
          aria-label={item.isCollapsed ? 'Expand' : 'Collapse'}
          aria-expanded={item.isCollapsed ? 'false' : 'true'}
          /**
           * Tab index is set to -1 to prevent the item from being focused when the tree is navigated with tab
           * since we use the arrow keys instead
           */
          tabIndex={tabIndex}
        >
          {item.isCollapsed && <ChevronRightIconOutline className="ndl-icon" />}
          {!item.isCollapsed && <ChevronDownIconOutline className="ndl-icon" />}
        </button>
      )}
      {item.isSelected !== undefined && (
        <Skeleton
          onBackground="weak"
          shape="rectangular"
          {...item.skeletonProps}
          isLoading={item.isSkeletonLoading}
        >
          <Checkbox
            isIndeterminate={isIndeterminate}
            isChecked={item.isSelected}
            ariaLabel={item.data.text}
            htmlAttributes={{
              tabIndex,
            }}
            onChange={(event) => {
              const newItems = items.map((i) => {
                if (i.id === item.id) {
                  return {
                    ...i,
                    isSelected: event.target.checked,
                  };
                }
                return i;
              });

              // Uncheck parents if a child is unchecked
              if (!event.target.checked) {
                let parentId = item.parentId;
                while (parentId !== null) {
                  const parent = newItems.find(
                    (newItem) => newItem.id === parentId,
                  );
                  if (parent) {
                    newItems.forEach((newItem) => {
                      if (newItem.id === parent.id) {
                        newItem.isSelected = false;
                      }
                    });
                    parentId = parent.parentId;
                  } else {
                    parentId = null;
                  }
                }
              }
              // Check children if a parent is checked
              if (item.canHaveSubItems) {
                const childIdsToUncheck = items
                  .filter((i) => i.parentId === item.id)
                  .map((i) => i.id);
                while (childIdsToUncheck.length > 0) {
                  const childId = childIdsToUncheck.pop();
                  const child = items.find((i) => i.id === childId);
                  if (child) {
                    newItems.forEach((newItem) => {
                      if (newItem.id === child.id) {
                        newItem.isSelected = event.target.checked;
                      }
                    });
                    newItems
                      .filter((i) => i.parentId === child.id)
                      .forEach((i) => {
                        childIdsToUncheck.push(i.id);
                      });
                  }
                }
              }
              onItemsChanged(newItems, { item, reason: 'selected' });
            }}
            aria-label={item.data.text}
          />
        </Skeleton>
      )}

      <Skeleton
        onBackground="weak"
        shape="rectangular"
        height="20px"
        {...item.skeletonProps}
        isLoading={item.isSkeletonLoading}
      >
        <ConditionalWrap
          shouldWrap={item.data.onTextClick !== undefined}
          wrap={(children) => (
            <button
              type="button"
              onClick={item.data.onTextClick}
              className="ndl-tree-view-text-clickable"
              tabIndex={tabIndex}
            >
              {children}
            </button>
          )}
        >
          <Typography
            variant={item.canHaveSubItems ? 'subheading-small' : 'body-medium'}
            htmlAttributes={{
              style: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          >
            {item.data.text}
          </Typography>
        </ConditionalWrap>
      </Skeleton>
      <div className="ndl-tree-view-actions">
        {item.data.actions &&
          item.data.actions.map((action, index) => {
            return (
              <CleanIconButton
                key={index}
                className="ndl-tree-view-action"
                size="small"
                {...action.buttonProps}
                htmlAttributes={{
                  tabIndex,
                  ...action.buttonProps.htmlAttributes,
                }}
              >
                {action.icon}
              </CleanIconButton>
            );
          })}
      </div>
    </TreeItemWrapper>
  );
};
